import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Card, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { OrderFormData } from "@/components/screens/OrderFormPage/types";
import useDialog from "@/hooks/use-dialog";
import { IOrder, IOrderInput } from "@/interfaces/order.interfaces";
import { OrderDataTabMessages } from "@/messages/order-form-page/order-data-tab.messages";
import { SelectData } from "@/types/form.types";
import { OrderStatus } from "@/types/order.types";

import DeliveryDialog from "./components/DeliveryDialog";
import InvoicedDialog from "./components/InvoicedDialog";
import ShippedDialog from "./components/ShippedDialog";
import { STATUS_OPTIONS } from "../../constants";

type Props = {
  messages: OrderDataTabMessages;
  order: IOrder | null;
};

export default function OrderInfo({ messages, order }: Props) {
  const { values, errors, setFieldValue, handleChange } = useFormikContext<OrderFormData>();
  const [previousStatus, setPreviousStatus] = useState<OrderStatus | null>(null);

  const {
    isVisible: isInvoicedVisible,
    handleOpen: handleOpenInvoiced,
    handleClose: handleCloseInvoiced,
  } = useDialog<IOrderInput>();

  const {
    isVisible: isShippedVisible,
    handleOpen: handleOpenShipped,
    handleClose: handleCloseShipped,
  } = useDialog<IOrderInput>();

  const {
    isVisible: isDeliveredVisible,
    handleOpen: handleOpenDelivered,
    handleClose: handleCloseDelivered,
  } = useDialog<IOrderInput>();

  useEffect(() => {
    if (previousStatus === null) setPreviousStatus(values.orderStatus);
  }, [previousStatus, values.orderStatus]);

  const handleOpenCurrentStatusModal = () => {
    const { Invoiced, Shipped, Delivered } = OrderStatus;

    switch (values.orderStatus) {
      case Invoiced:
        handleOpenInvoiced();
        break;
      case Shipped:
        handleOpenShipped();
        break;
      case Delivered:
        handleOpenDelivered();
        break;
      default:
        break;
    }
  };

  const handleStatusChange = (
    _: React.SyntheticEvent,
    newValue: SelectData<OrderStatus> | null
  ) => {
    const newStatus = newValue?.value;

    if (!newStatus) {
      return;
    }

    const { Invoiced, Shipped, Delivered } = OrderStatus;

    if (newStatus === Invoiced && values.orderStatus !== Invoiced) {
      handleOpenInvoiced();
    } else if (newStatus === Shipped && values.orderStatus !== Shipped) {
      handleOpenShipped();
    } else if (newStatus === Delivered && values.orderStatus !== Delivered) {
      handleOpenDelivered();
    }

    setPreviousStatus(values.orderStatus);
    setFieldValue("orderStatus", newStatus);
  };

  const isExternalOrder = Boolean(values.customerPlatformId);
  // Quando houver data de cancelamento, exibir como Cancelado (L) ou CR
  const effectiveStatus = values.cancelationDate
    ? values.orderStatus === OrderStatus.CancellationRequest
      ? OrderStatus.CancellationRequest
      : OrderStatus.Cancelled
    : values.orderStatus;

  const statusOpts = STATUS_OPTIONS(
    messages,
    values.isEditing,
    effectiveStatus,
    previousStatus,
    isExternalOrder
  );

  const isCancelledLike = Boolean(values.cancelationDate);
  const isSelectDisabled = (statusOpts.length === 1 && !values.isEditing) || isCancelledLike;

  const shouldShowSearchIcon =
    values.orderStatus === OrderStatus.Invoiced ||
    values.orderStatus === OrderStatus.Shipped ||
    values.orderStatus === OrderStatus.Delivered;

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.orderInfoTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="orderNumber"
                label={messages.orderNumberLabel}
                value={values.orderNumber}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.orderNumber}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label={messages.orderDateLabel}
                value={dayjs(typeof values.orderDate === "string" ? values.orderDate : new Date())}
                onChange={(value) => setFieldValue("orderDate", value ? value.format() : "")}
                disabled
                slotProps={{
                  textField: {
                    helperText: errors.orderDate,
                    error: Boolean(errors.orderDate),
                    fullWidth: true,
                    variant: "standard",
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                disabled
                name="salesChannel"
                label={messages.channelLabel}
                value={values.salesChannel}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.salesChannel}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="salesRepresentative"
                label={messages.salesRepresentativeLabel}
                value={values.salesRepresentative || ""}
                onChange={handleChange}
                fullWidth
                type="number"
                InputLabelProps={{ shrink: true }}
                disabled
                error={!!errors.salesRepresentative}
                InputProps={{
                  sx: {
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }} display="flex" alignItems="center">
              <Autocomplete
                fullWidth
                disabled={!values.isEditing || isSelectDisabled}
                options={statusOpts}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={statusOpts.find((option) => option.value === effectiveStatus) || null}
                onChange={handleStatusChange}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label={messages.orderStatusLabel}
                    variant="standard"
                    error={!!errors.orderStatus}
                    helperText={errors.orderStatus}
                  />
                )}
              />
              {shouldShowSearchIcon && (
                <IconButton onClick={handleOpenCurrentStatusModal} sx={{ ml: 1 }}>
                  <SearchIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>

      <InvoicedDialog
        isVisible={isInvoicedVisible}
        handleClose={handleCloseInvoiced}
        messages={messages}
        disabled={order?.orderStatus === OrderStatus.Invoiced}
      />

      <ShippedDialog
        isVisible={isShippedVisible}
        handleClose={handleCloseShipped}
        messages={messages}
        disabled={order?.orderStatus === OrderStatus.Shipped}
      />

      <DeliveryDialog
        isVisible={isDeliveredVisible}
        handleClose={handleCloseDelivered}
        messages={messages}
        disabled={order?.orderStatus === OrderStatus.Delivered}
      />
    </Card>
  );
}
